import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import PopoverMenu from '@deltek/specification-client/core/Navigation/PopoverMenu'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    makeStyles,
    MenuItem,
    PopoverOrigin,
    Theme,
    Typography,
} from '@material-ui/core'
import clsx from 'clsx'
import React from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useBetaWorkspaceFlag } from 'shared/useBetaWorkspaceFlag'
import { WorkspaceActionBarContext } from './WorkspaceActionBarContext'
export interface MenuItemStyleProps {
    hasSmallTitle?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    actionLabel: {
        fontSize: pxToRem(13),
        color: (props: MenuItemStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#424242' : '#A78966',
    },
    moreIcon: {
        fill: '#A78966',
    },
    formSidebar: {
        minWidth: 240,
        maxWidth: 240,
    },
    actionButtonGrid: {
        width: '30%',
        position: 'relative',
        left: pxToRem(180),
        paddingTop: theme.spacing(1),
    },
    changesCard: {
        minWidth: pxToRem(234),
        maxWidth: pxToRem(234),
    },
    cardButton: {
        padding: 0,
    },
    changesList: {
        padding: theme.spacing(1.75),
        paddingTop: 0,
    },
    noChangesText: {
        color: ' rgba(0, 0, 0, 0.5)',
        fontSize: pxToRem(14),
    },
    actionMenuItem: {
        padding: (props: MenuItemStyleProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? theme.spacing(0.625, 0)
                : theme.spacing(0.5, 0),
        paddingRight: ({ hasSmallTitle }: MenuItemStyleProps) =>
            hasSmallTitle ? theme.spacing(6) : 'none',
    },
    changesGridItem: {
        margin: 'auto',
        paddingTop: theme.spacing(1),
        maxHeight: pxToRem(500),
        overflow: 'auto',
        maxWidth: pxToRem(265),
    },
    cardSkeleton: {
        marginLeft: theme.spacing(1.5),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    smallLabel: {
        position: 'absolute',
        right: 0,
        fontSize: theme.spacing(1),
        color: theme.palette.grey[700],
    },
}))

export interface CompleteMenuItemConfig extends MenuItemConfig {
    isDisabled?: boolean
    isHidden?: boolean
    smallLabel?: string
}

export interface WorkspacePopoverProps {
    menuItems: CompleteMenuItemConfig[]
    anchorElement: Element | null
    isActionPopoverDisplayed: boolean
    handleActionPopoverClick: () => void
    anchorOrigin?: PopoverOrigin
    customTextStyle?: string
    transformOrigin?: PopoverOrigin
    disableReviewer?: boolean
}

export const WorkspacePopover: React.FC<WorkspacePopoverProps> = ({
    menuItems,
    anchorElement,
    isActionPopoverDisplayed,
    handleActionPopoverClick,
    anchorOrigin,
    customTextStyle,
    transformOrigin,
    disableReviewer,
}) => {
    const hasSmallTitle = React.useMemo(() => {
        return menuItems.some((item) => !!item.smallLabel)
    }, [menuItems])
    const { currentState } = React.useContext(WorkspaceActionBarContext)
    // hook to check if the beta workspace is enabled using url params
    const { isBetaWorkspaceEnabled } = useBetaWorkspaceFlag()
    const { previewFeatures } = usePreviewFeatures()
    // checking if the redesigned toggle on show/hide bar is enabled
    const isBetaWorkspaceToggleEnabled =
        (previewFeatures.hasNewWorkspace2 ||
            isBetaWorkspaceEnabled) &&
        currentState?.showHideStates?.betaWorkspace
    const classes = useStyles({
        hasSmallTitle: hasSmallTitle,
        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
    })

    return (
        <PopoverMenu
            data-testid="trackChangesPopover"
            open={isActionPopoverDisplayed}
            anchorEl={anchorElement}
            marginLeft={
                menuItems?.[0]?.dataTestId === 'all-project-elements' &&
                    isBetaWorkspaceToggleEnabled
                    ? 30
                    : 0
            }
            onClose={handleActionPopoverClick}
            anchorOrigin={anchorOrigin}
            transformOrigin={
                transformOrigin
                    ? transformOrigin
                    : {
                        vertical: 'bottom',
                        horizontal: 'right',
                    }
            }
        >
            {menuItems.map((menuItem: CompleteMenuItemConfig, index) => {
                if (menuItem?.isHidden) return null

                return !!menuItem.component ? (
                    menuItem.component
                ) : (
                    <MenuItem
                        key={menuItem.dataTestId}
                        data-testid={menuItem.dataTestId}
                        onClick={menuItem?.onClick as () => void}
                        disabled={menuItem?.isDisabled || disableReviewer}
                        className={clsx(
                            customTextStyle
                                ? customTextStyle
                                : classes.actionLabel,
                            classes.actionMenuItem
                        )}
                    >
                        {menuItem?.label ?? ''}
                        {menuItem?.smallLabel && (
                            <Typography
                                component="span"
                                className={clsx(classes.smallLabel)}
                            >
                                {menuItem?.smallLabel}
                            </Typography>
                        )}
                    </MenuItem>
                )
            })}
        </PopoverMenu>
    )
}
